import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  message,
  Popconfirm,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Spin,
  Empty,
  Tooltip
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  PlusOutlined,
  CalendarOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { newsService } from "../../services/newsService";

const { Title, Text } = Typography;
const { TextArea } = Input;

function Newsform(props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsData, setNewsData] = useState([]);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await newsService.getAllNews();
      setNewsData(data);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch news data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values) => {
    try {
      await newsService.createNews(values);
      message.success('News added successfully');
      fetchData();
      form.resetFields();
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.data || 'Failed to add news');
      } else {
        message.error('Failed to add news');
      }
    }
  };

  const handleEdit = (record) => {
    setEditingNews(record);
    form.setFieldsValue({
      title: record.title,
      content: record.content
    });
    setModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      await newsService.updateNews(editingNews._id, values);
      message.success('News updated successfully');
      setModalVisible(false);
      setEditingNews(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error('Failed to update news');
    }
  };

  const handleDelete = async (id) => {
    try {
      await newsService.deleteNews(id);
      message.success('News deleted successfully');
      fetchData();
    } catch (error) {
      message.error('Failed to delete news');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingNews(null);
    form.resetFields();
  };

  // Statistics
  const stats = {
    total: newsData.length,
    active: newsData.filter(news => news.title && news.content).length
  };

  // Table columns
  const columns = [
    {
      title: 'Titel',
      dataIndex: 'title',
      key: 'title',
      render: (title) => (
        <Text strong style={{ fontSize: '16px' }}>
          {title || 'Unbenannte Nachricht'}
        </Text>
      ),
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      ellipsis: true,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
    },
    {
      title: 'Inhalt',
      dataIndex: 'content',
      key: 'content',
      render: (content) => (
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {content && content.length > 100 ? `${content.substring(0, 100)}...` : content || 'Kein Inhalt'}
        </Text>
      ),
      ellipsis: true,
      responsive: ['sm', 'md', 'lg', 'xl']
    },
    {
      title: 'Erstellt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString('de-DE') : '',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      ellipsis: true,
      responsive: ['md', 'lg', 'xl']
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Details anzeigen">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                Modal.info({
                  title: record.title || 'Unbenannte Nachricht',
                  content: (
                    <div>
                      <p><strong>Titel:</strong> {record.title || 'Unbenannte Nachricht'}</p>
                      <p><strong>Inhalt:</strong></p>
                      <div style={{ 
                        background: '#f5f5f5', 
                        padding: '12px', 
                        borderRadius: '6px',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {record.content || 'Kein Inhalt vorhanden'}
                      </div>
                    </div>
                  ),
                  width: 500
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Möchten Sie diese Nachricht wirklich löschen?"
            onConfirm={() => handleDelete(record._id)}
            okText="Ja"
            cancelText="Nein"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Tooltip title="Löschen">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
    }
  ];

  return (
    <div style={{ padding: '24px', background: 'var(--app-bg, #f5f5f5)', minHeight: '100vh', maxWidth: '100vw', margin: '0 auto' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Nachrichtenverwaltung
            </Title>
            <Text type="secondary">Verwalten Sie die Nachrichten und Ankündigungen Ihrer Praxis</Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setModalVisible(true)}
            >
              Nachricht hinzufügen
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <Statistic
              title="Gesamtanzahl Nachrichten"
              value={stats.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#41b06e' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="Vollständige Nachrichten"
              value={stats.active}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* News Table */}
      <Card>
        <Spin spinning={loading}>
          {newsData.length === 0 ? (
            <Empty
              description="Keine Nachrichten gefunden"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={() => setModalVisible(true)}>
                Erste Nachricht hinzufügen
              </Button>
            </Empty>
          ) : (
            <Table
              columns={columns}
              dataSource={newsData}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} von ${total} Einträgen`
              }}
              style={{ width: '100%' }}
            />
          )}
        </Spin>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingNews ? 'Nachricht bearbeiten' : 'Neue Nachricht hinzufügen'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingNews ? handleUpdate : handleSubmit}
        >
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Bitte geben Sie den Titel ein' }]}
          >
            <Input placeholder="Titel der Nachricht eingeben" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Inhalt"
            rules={[{ required: true, message: 'Bitte geben Sie den Inhalt ein' }]}
          >
            <TextArea
              rows={6}
              placeholder="Nachrichteninhalt eingeben"
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalClose}>
                Abbrechen
              </Button>
              <Button type="primary" htmlType="submit">
                {editingNews ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Newsform;
